// How much of a drum a plan can still draw on.
//
// ONE rule, because the wizard had three and they disagreed. Reported
// 2026-10-23 on three CYYF 3x2.5 lots (6adb0be3…, 6ada295b…0137, …0136): each
// had 15 000 m of its 20 000 m planned into a rope, each had ~5 000 m left, and
// the drum that was still on the machine could not be selected as an input.
// "in mod normal ar trebuii sa pot selecta bobinele care au label in production
// daca mai este spatiu liber pe ele, adica planned - used mai ramane ceva."
//
// The three rules were:
//
//   the card          cap = availableLength || producedLength || plannedLength
//   toggleSelect      cap = exec.plannedLength
//   the Add button    cap = exec.plannedLength, used = max(reserved, consumed)
//
// So a drum could be IN the selection (toggleSelect said 5 000 m left) while the
// card drew it as finished and forced its checkbox off — `isSelected` is
// `…some(e => e.id === exec.id) && !finished`. Clicking it did select it
// invisibly, which is why the rope's "all colours present" check then passed and
// "+ Add Rope Execution" lit up with two colours visibly ticked. Both halves of
// what the user saw, from one disagreement.
//
// ── why `availableLength` is not used here ─────────────────────────────────
//
// It has two writers with two meanings:
//
//   autoComplete/luaScripts.js:563   availableLength = producedLength   (gross)
//   helpers/inputConsumption.js      availableLength = produced − consumed
//   controller/jobs.js:2831          availableLength −= reserved        (net)
//
// Whichever ran last decides. And `used` below already counts what the plan has
// taken, so subtracting a net `availableLength` as well removes the same metres
// twice: at the time of the report the worker had just ticked, so the blue drum
// read 20 000.5 and behaved; by the time its reservation was re-applied it read
// 5 000.5 and EVERY drum of that product would have gone dead. The capacity here
// is built from `producedLength` and the plan, which no second writer redefines.
//
// Pure, and mirrored into tests/.generated by tests/tools/mirror.mjs.

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

const DONE = new Set(['DONE', 'COMPLETED', 'FINISHED']);

/**
 * Is this drum finished growing?
 *
 * A drum off the machine holds what it holds. One still on it will reach its
 * planned length, and planning against the partial figure is what blocked the
 * three lots above — its own PENDING sibling, which has produced nothing at
 * all, was plannable the whole time.
 */
export function drumIsComplete(token) {
  if (!token) return false;
  const status = String(token.status || '').toUpperCase();
  if (DONE.has(status)) return true;
  const planned = num(token.plannedLength);
  return planned > 0 && num(token.producedLength) >= planned;
}

/**
 * What the drum will hold in total.
 *
 * Complete  — what it actually holds. NOT the plan: a CYYF 4x16 rope drum
 *             planned for 4 120 m that came off at 2 108.21 must offer 2 108.21,
 *             or the next phase is planned over 2 012 m of cable that does not
 *             exist.
 * Running   — the greater of its plan and what is already on it. The over-run
 *             counts (23 660 m against 20 000 is routine) and so do the metres
 *             not yet wound.
 * No token  — create-new: nothing is made yet, so the plan is all there is.
 */
export function drumCapacity(exec, token) {
  // Coiling and rewinding state their length as a count times a length, and
  // carry no `plannedLength` at all. The card knew that and `toggleSelect` did
  // not, which is the same disagreement in miniature.
  const bundled =
    exec?.coilCount !== undefined && exec?.coilLength !== undefined
      ? num(exec.coilCount) * num(exec.coilLength)
      : exec?.rewindCount !== undefined && exec?.rewindLength !== undefined
        ? num(exec.rewindCount) * num(exec.rewindLength)
        : 0;

  // No drum yet — create-new. The plan is all there is.
  if (!token) {
    return bundled || num(exec?.plannedLength) || num(exec?.plannedLengthInput) || 0;
  }

  // A DRUM THAT EXISTS ANSWERS FOR ITSELF. The phasePlan's figure is not
  // consulted at all here, and that is the point: a drum forked away to another
  // product is kept as a husk with produced 0 and planned 0, and falling back
  // to the plan behind it offered the 15 000 m it had just given away — on
  // CYABYF 4x1.5, three husks went from unselectable to selectable, which would
  // have launched a rope on drums belonging to the fork. The plan is a wish; the
  // token is the drum.
  const produced = num(token.producedLength);
  const planned = bundled || num(token.plannedLength);

  if (drumIsComplete(token)) return Math.max(produced, bundled);
  return Math.max(planned, produced);
}

/**
 * Metres of this drum a new execution can still be planned against.
 *
 * `used` is what the plan has already drawn from it — every execution of the
 * phase being planned, the ones already launched included (continue mode
 * rebuilds them into `phase.execution`, so `buildConsumedMapForPhase` counts
 * them). That is the same 15 000 m the token carries as `reservedLength`,
 * counted once, from the side that also sees edits made in this session.
 */
export function drumRemaining({ exec, token, used = 0 }) {
  return Math.max(drumCapacity(exec, token) - num(used), 0);
}

/**
 * Metres already planned OFF a drum — by earlier sessions, not this one.
 *
 * A drum names no children; the children name the drum, in `parents`. A child
 * fed by several drums at once (a flat-cable sheath, a rope) records what it
 * took from each in `parentTakes` — without that, a 25 km run counts as 25 km
 * against every drum it touched, including one it only took 5 km from.
 *
 * This is the rule the incoming-execution cards already used to show "Used";
 * it lives here so the PROGRESS BAR can start from the same number instead of
 * from zero. Reported 2026-10-23: after planning 15 000 m of a 20 000 m lot and
 * coming back for the rest, the rope bar read "5 000 / 20 000" with 15 000
 * already committed. "nu ar putea barul asta sa plece cumva de la 15000 / 20000".
 */
export function plannedFromDrum(tokens, tokenId) {
  const id = String(tokenId ?? '');
  if (!id) return 0;
  let total = 0;
  for (const child of Object.values(tokens || {})) {
    if (child?.cancelled) continue;
    const parents = Array.isArray(child?.parents) ? child.parents.map(String) : [];
    if (!parents.includes(id)) continue;
    const take = child?.parentTakes?.[id];
    total += take != null ? num(take) : estimatedTake(tokens, child, parents, id);
  }
  return total;
}

/**
 * What a child took off ONE of its parents, when it never said.
 *
 * `parentTakes` is only written by a launch that knows the split, so every drum
 * made before that existed — and every one made by a service that has not been
 * restarted since — falls through to here. The fallback used to be the child's
 * FULL length against every parent, and that is the number which made the
 * incoming-execution cards read "Used 15 000" on a 10 000 m drum: a drum
 * claiming more than it holds has nothing left, so `toggleSelect` refuses to
 * tick it and the planner cannot take a leftover plus a fresh drum into one
 * run. Reported 2026-10-29, as the other face of the 2026-10-28 report.
 *
 * The rule is the one in redis_dev/helpers/inputTakes.js, which is the
 * authority: a run draws its length from each COLOUR, split across that
 * colour's drums in order. So among the parents sharing this parent's colour,
 * the child's length is handed out in order, each giving at most what it holds.
 * Parents of other colours are not this parent's business.
 *
 * A guess, and deliberately a conservative one — it can never bill a parent
 * more than the parent holds, which is the direction that was doing harm.
 */
function estimatedTake(tokens, child, parents, parentId) {
  const colourOf = (id) => {
    const c = tokens?.[id]?.color;
    return c == null ? '' : String(c).trim().toUpperCase();
  };
  const holds = (id) => {
    const t = tokens?.[id];
    return Math.max(0, Math.max(num(t?.producedLength), num(t?.plannedLength)));
  };

  // The parents feeding the same position as this one, in their stated order.
  const mine = colourOf(parentId);
  const siblings = parents.filter((p) => colourOf(p) === mine);
  if (siblings.length < 2) return num(child.plannedLength);

  let left = num(child.plannedLength);
  for (const p of siblings) {
    const take = Math.min(holds(p), left);
    if (String(p) === String(parentId)) return Math.max(0, take);
    left = Math.max(0, left - take);
  }
  return 0;
}

/** Metres already planned INTO a phase, across every drum it will make. */
export function plannedForPhase(tokens, phaseName) {
  const name = String(phaseName ?? '');
  if (!name) return 0;
  let total = 0;
  for (const token of Object.values(tokens || {})) {
    if (token?.cancelled) continue;
    if (String(token?.phase ?? '') !== name) continue;
    total += num(token.plannedLength);
  }
  return total;
}

/**
 * Metres of a drum that are already spoken for, from every side at once.
 *
 * Four sources, and the largest wins because they are four VIEWS of the same
 * commitment rather than four separate ones:
 *
 *   phasePlanned  what the executions in this wizard session draw from it
 *                 (`buildConsumedMapForPhase`) — the only one that follows an
 *                 edit made right now
 *   reservedLength / consumedLength  what the backend recorded
 *   children      what earlier sessions planned off it (`plannedFromDrum`)
 *
 * Adding them would count one reservation up to four times. The incoming cards
 * had this rule inline and the phase's own gates did not, which is why the Plan
 * button typed 20 000 into a run that could only be 5 000.
 */
export function usedOnDrum({ exec, tokens, phasePlanned = 0 } = {}) {
  const token = exec?.tokenId ? tokens?.[exec.tokenId] : null;
  if (!token) return num(phasePlanned);
  return Math.max(
    num(phasePlanned),
    num(token.reservedLength),
    num(token.consumedLength),
    plannedFromDrum(tokens, exec.tokenId)
  );
}

/**
 * What the "Plan" button should write.
 *
 * It wrote something different in every phase — the whole lot at
 * conductor_isolation, the upstream drum's full capacity at the sheaths, the
 * rope's TOTAL need at the rope (ignoring the 15 000 m already planned off it),
 * and `min(capacity, estimate)` only at rewinding. "acuma asta pare sa
 * functioneze diferit in fiecare faza."
 *
 * One meaning now: THE LARGEST LENGTH THAT WILL ACTUALLY FIT. Whichever of
 * these is smallest, because each is a real ceiling —
 *
 *   inputsRemaining  what the drums behind it can still give
 *   phaseRemaining   what the product still needs of this phase
 *   drumHolds        what the drum being wound can physically take
 *
 * An ABSENT ceiling is not a ceiling — a phase with no total of its own passes
 * nothing for `phaseRemaining`. A ceiling of ZERO is a ceiling, and a closed
 * one: nothing can be planned. The difference is the whole correctness of this
 * function and the first draft got it wrong by filtering on `> 0` — on three
 * CYYF 3x2.5 lots whose cores were fully committed it dropped
 * `inputsRemaining: 0` as absent and offered the drum's 14 390 m.
 */
export function planLength({ inputsRemaining, phaseRemaining, drumHolds } = {}) {
  const caps = [inputsRemaining, phaseRemaining, drumHolds]
    .filter(v => v != null && Number.isFinite(Number(v)))
    .map(num);
  if (!caps.length) return 0;
  const min = Math.min(...caps);
  return min > 0 ? Math.round(min * 10) / 10 : 0;
}

/**
 * How many drums of one length still fit — the "× N" on an execution.
 *
 * Asked for 2026-10-23: 20 000 m of MYYM 3x2.5 to be wound as 40 drums of
 * 500 m, which meant adding forty executions by hand. One execution and a
 * count instead.
 *
 * The tolerance is there because these lengths are typed by people and measured
 * by machines: 20 000 / 500 must be 40 even when the drum holds 19 999.97.
 */
export function duplicateCount({ remaining, each } = {}) {
  const per = num(each);
  const left = num(remaining);
  if (!(per > 0) || !(left > 0)) return 0;
  return Math.floor((left + per * 0.001) / per);
}

/**
 * What a run can still be, given everything it consumes at once.
 *
 * A rope metre costs a metre of EVERY core; a flat sheath metre costs a metre of
 * every colour. So what is plannable is the SHORTEST input, not the sum of them
 * — summing keeps "Add execution" enabled on a selection with brown left and no
 * blue, and then the length allocator rejects every value typed.
 *
 * Phases that draw their metres from one drum at a time (a sheath over a rope,
 * coiling) add up instead: those inputs are alternatives, not a set.
 *
 * @param inputs      the selected upstream executions
 * @param tokenOf     exec -> its token, or null
 * @param usedOf      exec -> metres the plan has already drawn from it
 * @param everyInput  true for rope / pre-stranding / flat sheath
 */
export function plannableRemaining({
  inputs = [],
  tokenOf = () => null,
  usedOf = () => 0,
  everyInput = false
}) {
  const list = Array.isArray(inputs) ? inputs.filter(Boolean) : [];
  if (!list.length) return 0;

  const each = list.map(exec =>
    drumRemaining({ exec, token: tokenOf(exec), used: usedOf(exec) })
  );

  if (!everyInput) return each.reduce((s, v) => s + v, 0);

  // Grouped first: two drums of the same supply feeding one run are ONE supply
  // (the second carries on where the first ran out), and a rope needs every
  // supply to have some — so the groups are summed and the groups minimised.
  //
  // The key used to fall back to `#${i}` when a drum had no colour, which is
  // not a model of anything: it made every colourless drum its own supply, so
  // `Math.min` answered with a SINGLE drum. Copper has no colour — stock reels
  // carry `color: undefined`, rope and pre-stranding tokens `color: null` — so
  // on every phase fed by copper, two drums of 5 000 m offered 5 000 and a
  // 6 000 m run was refused. Reported 2026-10-29: "nu ma lasa sa folosesc 2
  // inputi in momentul in care unu nu imi ajunge".
  //
  // Colourless drums are told apart by DIAMETER instead, which is what
  // distinguishes a wire position on a strand. With neither, they are one
  // supply — summed, which is the sequential case and the one the planner is
  // asking about.
  const bySupply = new Map();
  list.forEach((exec, i) => {
    const colour = exec?.color == null ? '' : String(exec.color).trim().toUpperCase();
    const diam = num(exec?.wireDiameter ?? exec?.diameter);
    const key = colour || (diam > 0 ? `Ø${diam}` : '-');
    bySupply.set(key, (bySupply.get(key) || 0) + each[i]);
  });
  return Math.min(...bySupply.values());
}
